import {
  createContext, useContext, useState, useCallback,
  type FC, type ReactNode,
} from "react";
import type { User } from "../types/auth";
import { authenticate } from "./credentials";
import { catalystLogin } from "../api/catalystPortal";

const SESSION_KEY = "gew_dashboard_user";

interface AuthContextValue {
  user: User | null;
  /**
   * Async now. Tries the hardcoded staff credentials first (synchronous — no
   * network needed), then falls back to the backend catalyst login. Returns
   * true on success; false means neither path recognised the credentials.
   */
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────
export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Rehydrate from sessionStorage so a page refresh keeps the user logged in
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  });

  const persist = (u: User) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(u));
    setUser(u);
  };

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    // 1. Staff (hardcoded) — cheap, no network.
    const staff = authenticate(username, password);
    if (staff) {
      persist(staff);
      return true;
    }

    // 2. Catalyst (backend) — only tried if it looks like an email, so a bad
    //    staff typo doesn't trigger a wasted network call.
    if (!username.includes("@")) return false;
    try {
      const res = await catalystLogin(username, password);
      persist({
        role: "catalyst",
        label: res.catalyst.fullNameAr || res.catalyst.email,
        id: res.catalyst.id,
        email: res.catalyst.email,
        accessToken: res.accessToken,
        governorate: res.catalyst.governorate,
        cityArea: res.catalyst.cityArea,
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ── Hook ───────────────────────────────────────────────────────────────────────
export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
